import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Select, Text } from '~/components';
import type { RootStackParamList } from '~/navigation/types';
import * as yup from 'yup';
import { marisaTakesCareResumeItems } from '~/utils/services';
import { TakesCareComponents } from '~/components/TakesCare';
import GeneralConditionsButton from '../../components/GeneralConditionsButton';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useForm } from 'react-hook-form';
import { Components } from '~/scenes/HiredInsurance/components';
import { cancelContractMarisaTakesCareUseCase } from '~/useCases/MarisaTakesCare/CancelContractMarisaTakesCareUseCase';
import DefaultContainer from '../../components/DefaultContainer';
import { GENERAL_CONDITIONS_LINK } from '~/scenes/MarisaTakesCare/constants';
import useToast from '~/hooks/useToast';
import { yupResolver } from '@hookform/resolvers/yup';
import { getCancelReasonsUseCase } from '~/useCases/AssegurouUseCases/GetCancelReasonsUseCase';
import { HiredInsuranceProductType } from '~/domain/Assegurou/contractedServices';

export const schema = yup
  .object()
  .shape({
    cancelReasonId: yup.string().required('Este campo é obrigatório'),
  })
  .required();

const HiredInsuranceCancelTakesCare: React.FC = () => {
  const { goBack, navigate } = useAppNavigation();
  const { showError } = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ cancelReasonId: string }>({
    resolver: yupResolver(schema),
  });

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<
        RootStackParamList,
        'MarisaServicos_HiredInsuranceCancelTakesCare'
      >
    >();

  const [loading, setLoading] = useState(true);
  const [cancelReasons, setCancelReasons] = useState<
    { label: string; value: string }[] | []
  >([]);

  const cancelContract = useCallback(
    async (data: { cancelReasonId: string }) => {
      const responseSuccess = await cancelContractMarisaTakesCareUseCase({
        certificateCode,
        cancelReasonCode: data.cancelReasonId,
      });

      if (responseSuccess) {
        navigate('MarisaServicos_HiredInsuranceCancelSuccessTakesCare');
      } else {
        navigate('MarisaServicos_HiredInsuranceCancelFailTakesCare', {
          id: certificateCode,
        });
      }
    },
    [certificateCode, navigate]
  );

  const fetchCancelReasons = useCallback(async () => {
    setLoading(true);
    try {
      const cancelReasonsResponse = await getCancelReasonsUseCase(
        HiredInsuranceProductType.MARISA_TAKES_CARE
      );
      const formattedCancelReasons = cancelReasonsResponse.map((reason) => ({
        label: reason.description,
        value: reason.code,
      }));
      setCancelReasons(formattedCancelReasons);
    } catch (error) {
      showError({
        title: 'Erro',
        description:
          'Houve um erro ao tentar buscar os motivos do cancelamento. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchCancelReasons();
  }, [fetchCancelReasons]);

  return (
    <DefaultContainer
      type="marisa-cuida"
      className="py-[42px] items-center justify-center"
    >
      <Text className="text-gray-600 text-lg text-center">Cancelamento</Text>

      <Components.CancelStatus type="alert" />

      <Text className="text-lg text-gray-700 text-center mb-6">
        Você quer mesmo cancelar o Assistência{' '}
        <Text className="text-primary-600">Marisa Cuida</Text>?
      </Text>

      <Text className="mt-5 text-center text-sm text-gray-400 mb-5">
        Você deixará de ter as seguintes coberturas:
      </Text>

      {marisaTakesCareResumeItems.map((item, index) => (
        <TakesCareComponents.ResumeItem {...item} key={index} />
      ))}

      <GeneralConditionsButton className="my-5" url={GENERAL_CONDITIONS_LINK} />

      <Select
        control={control}
        loading={loading}
        className="text-sm mb-4"
        label="Caso queira mesmo cancelar, selecione uma opção abaixo:"
        name="cancelReasonId"
        placeholder="Selecione o motivo do cancelamento"
        error={errors.cancelReasonId?.message}
        items={cancelReasons}
        containerStyle={{ flex: 0.3 }}
      />

      <Button className="w-full" onPress={handleSubmit(cancelContract)}>
        Cancelar Seguro
      </Button>

      <Button variant="tertiary" className="mt-4" onPress={goBack}>
        VOLTAR
      </Button>
    </DefaultContainer>
  );
};

export default HiredInsuranceCancelTakesCare;
